import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { AdminDetailTable } from "@/views/components/DetailTable";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";

export interface CustomerCommunicationRecord {
    id?: number | string;
    customerId?: number | string;
    communicateDate?: string;
    communicateManner?: number | string;
    contactFlag?: number | string | boolean;
    communicateDuration?: number | string;
    communicateSummary?: string;
    createName?: string;
    createdBy?: number | string;
}

export interface CustomerCommunicationQuery {
    customerId: number;
    page: number;
    size: number;
    keyword?: string;
    communicateBeginTime?: string;
    communicateEndTime?: string;
}

export interface CustomerCommunicationPageProps {
    customerId: number;
    currentUserId?: number;
    canRead?: boolean;
    canWrite?: boolean;
    loadRecords?: (
        query: CustomerCommunicationQuery
    ) => Promise<{ list?: CustomerCommunicationRecord[]; total?: number }>;
    onAdd?: () => void;
    onEdit?: (id: number) => void;
    onDetail?: (id: number) => void;
}

const mannerLabels: Record<number, string> = { 1: "电话沟通", 2: "微信沟通", 3: "现场拜访" };

/** 复用 Vue `/admin/communicate-record/paging` 用户沟通记录分页接口。 */
export async function loadCustomerCommunicationRecords(query: CustomerCommunicationQuery) {
    const response = await request.service.get("/admin/communicate-record/paging", { params: query });
    return response.data as { list?: CustomerCommunicationRecord[]; total?: number };
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 19) : DEFAULT_VALUE);

export function CustomerCommunicationPage({
    customerId,
    currentUserId,
    canRead = true,
    canWrite = true,
    loadRecords = loadCustomerCommunicationRecords,
    onAdd,
    onEdit,
    onDetail
}: CustomerCommunicationPageProps) {
    const navigate = useNavigate();
    const loggedInUserId = useUserStore(state => state.userInfo.user?.id);
    // 旧 Vue 页面始终按当前登录用户限制编辑入口，未能解析用户 ID 时不能放开写操作。
    const effectiveCurrentUserId = currentUserId ?? (loggedInUserId == null ? undefined : Number(loggedInUserId));
    const [records, setRecords] = useState<CustomerCommunicationRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [keyword, setKeyword] = useState("");
    const [communicateBeginTime, setCommunicateBeginTime] = useState("");
    const [communicateEndTime, setCommunicateEndTime] = useState("");
    // 对齐旧 Vue ProTable 的分页状态，翻页和切换条数都进入沟通记录接口。
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [submittedQuery, setSubmittedQuery] = useState<Partial<CustomerCommunicationQuery>>({});
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        const query: CustomerCommunicationQuery = {
            customerId,
            page,
            size: pageSize,
            ...submittedQuery
        };
        void loadRecords(query)
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) {
                    setRecords([]);
                    setTotal(0);
                    setErrorMessage("沟通记录加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [customerId, loadRecords, page, pageSize, submittedQuery]);

    const submitSearch = () => {
        // 保持 Vue 的日期范围参数命名，并过滤空值后再请求分页接口。
        const nextQuery: Partial<CustomerCommunicationQuery> = {};
        if (keyword.trim()) nextQuery.keyword = keyword.trim();
        if (communicateBeginTime) {
            nextQuery.communicateBeginTime = communicateBeginTime.includes(":")
                ? communicateBeginTime
                : `${communicateBeginTime} 00:00:00`;
        }
        if (communicateEndTime) {
            nextQuery.communicateEndTime = communicateEndTime.includes(":")
                ? communicateEndTime
                : `${communicateEndTime} 23:59:59`;
        }
        setPage(1);
        setSubmittedQuery(nextQuery);
    };

    const resetSearch = () => {
        // 旧 Vue ProTable 重置会同时清空草稿条件、已提交条件并回到第一页。
        setKeyword("");
        setCommunicateBeginTime("");
        setCommunicateEndTime("");
        setSubmittedQuery({});
        setPage(1);
    };

    /** 将旧 Vue daterange 的格式化结果同步到沟通记录查询条件。 */
    const updateCommunicateTimeRange = (_value: unknown, formattedValue: [string, string] | null) => {
        setCommunicateBeginTime(formattedValue?.[0] || "");
        setCommunicateEndTime(formattedValue?.[1] || "");
    };

    const goAdd = () => {
        if (onAdd) {
            onAdd();
            return;
        }
        navigate(`/lianYunUser/AddCommunicateRecord?userId=${customerId}`);
    };

    const goEdit = (id: number) => {
        if (onEdit) {
            onEdit(id);
            return;
        }
        navigate(`/lianYunUser/editCommunicateRecord?id=${id}`);
    };

    const goDetail = (id: number) => {
        if (onDetail) {
            onDetail(id);
            return;
        }
        navigate(`/lianYunUser/communicateRecordDetail?id=${id}`);
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 沟通记录列表的字段和权限操作。
    const columns: TableColumnProps<CustomerCommunicationRecord>[] = [
        { key: "id", label: "编号", width: 170, prop: "id", render: ({ row }) => row.id ?? DEFAULT_VALUE },
        {
            key: "communicateDate",
            label: "沟通日期",
            width: 170,
            prop: "communicateDate",
            render: ({ row }) => formatDate(row.communicateDate)
        },
        {
            key: "communicateManner",
            label: "沟通方式",
            width: 170,
            prop: "communicateManner",
            render: ({ row }) => mannerLabels[Number(row.communicateManner)] || DEFAULT_VALUE
        },
        {
            key: "contactFlag",
            label: "是否联系上用户",
            width: 170,
            prop: "contactFlag",
            render: ({ row }) => (Number(row.contactFlag) === 1 || row.contactFlag === true ? "是" : "否")
        },
        {
            key: "communicateDuration",
            label: "沟通时长",
            width: 170,
            prop: "communicateDuration",
            render: ({ row }) => `${row.communicateDuration ?? DEFAULT_VALUE} min`
        },
        {
            key: "communicateSummary",
            label: "沟通总结",
            prop: "communicateSummary",
            render: ({ row }) => row.communicateSummary || DEFAULT_VALUE
        },
        {
            key: "createName",
            label: "创建人",
            width: 170,
            prop: "createName",
            render: ({ row }) => row.createName || DEFAULT_VALUE
        },
        {
            key: "operation",
            label: "操作",
            width: 120,
            prop: "operation",
            fixed: "right",
            render: ({ row }) => {
                const id = Number(row.id);
                const canEdit =
                    canWrite &&
                    effectiveCurrentUserId != null &&
                    Number(row.createdBy) === effectiveCurrentUserId;
                return (
                    <Space>
                        {canEdit ? (
                            <Button aria-label={`编辑 ${row.id}`} type="link" onClick={() => goEdit(id)}>
                                编辑
                            </Button>
                        ) : null}
                        {canRead ? (
                            <Button aria-label={`查看详情 ${row.id}`} type="link" onClick={() => goDetail(id)}>
                                查看详情
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="customer-communication-page" className="section-card customer-communication-page">
            <h2 className="section-title">沟通记录</h2>
            <AdminDetailTable<CustomerCommunicationRecord>
                data={records}
                columns={normalizeOperationColumns(columns)}
                emptyText="暂无数据"
                emptyBodyMinHeight={440}
                pagination={false}
                rowKey={row => String(row.id || "")}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header customer-communication-table-header">
                        {canWrite ? (
                            <Button type="primary" onClick={goAdd}>
                                新增
                            </Button>
                        ) : null}
                        <Input
                            aria-label="关键词"
                            placeholder="请输入关键词"
                            value={keyword}
                            onChange={event => setKeyword(event.target.value)}
                        />
                        <ZhDateRangePicker
                            clearable
                            endPlaceholder="沟通结束日期"
                            format="YYYY-MM-DD"
                            rangeSeparator="-"
                            startPlaceholder="沟通开始日期"
                            type="daterange"
                            value={[communicateBeginTime, communicateEndTime]}
                            valueFormat="YYYY-MM-DD HH:mm:ss"
                            onChange={updateCommunicateTimeRange}
                        />
                        <Button
                            aria-label="查询沟通记录 搜索"
                            icon={<TableSearchIcon name="search" />}
                            type="primary"
                            onClick={submitSearch}
                        >
                            搜索
                        </Button>
                        <Button
                            aria-label="重置沟通记录筛选"
                            className="customer-communication-table-header__reset"
                            icon={<TableSearchIcon name="reset" />}
                            onClick={resetSearch}
                        />
                    </div>
                }
                append={
                    total > 0 ? (
                    <div className="admin-table-composed-pagination">
                        <ZhPagination
                            current={page}
                            pageSize={pageSize}
                            total={total}
                            pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                            hideOnSinglePage
                            onChange={(nextPage, nextPageSize) => {
                                // 切换条数时回到第一页，沿用 Vue ProTable 的分页请求契约。
                                setPage(nextPageSize !== pageSize ? 1 : nextPage);
                                setPageSize(nextPageSize);
                            }}
                        />
                    </div>
                    ) : null
                }
            />
            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
        </section>
    );
}
